import { useBankingAccounts } from "Api/Queries/Accounts/useBankingAccounts";
import { usePayPointsQuery } from "Api/Queries/PayPoints/usePayPointsQuery";
import { InitiatePaymentForm } from "Components/Merchant/Initiate/InitiatePaymentForm/InitiatePaymentForm";
import { InitiatePaymentFormSkeleton } from "Components/Merchant/Initiate/InitiatePaymentFormSkeleton";
import { InitiatePaymentKeypad } from "Components/Merchant/Initiate/InitiatePaymentKeypad/InitiatePaymentKeypad";
import { InitiatePaymentKeypadSkeleton } from "Components/Merchant/Initiate/InitiatePaymentKeypad/InitiatePaymentKeypadSkeleton";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { useAmount } from "Hooks/useAmount";
import { useUserMarketCurrency } from "Hooks/useUserMarketCurrency";
import { useIsUserVerified } from "Hooks/useIsUserVerified";
import { hasPreApprovedAccount, pickMainBankAccount } from "Utils/AccountUtils";
import { useState, type ReactNode } from "react";
import type { FieldErrors } from "react-hook-form";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";
import { getTransactionErrorMessage } from "Utils/TransactionErrorsUtils";
import { ActiveTransactionConflictModal } from "Components/Merchant/Initiate/ActiveTransactionConflictModal";
import { useInitiatePaypointPayment } from "Hooks/useInitiatePaypointPayment";
import type { InitiatePaymentFormData } from "Components/Merchant/Initiate/InitiatePaymentForm/schema";

const VARIANT_TO_SKELETON: Record<PaymentInitiatorVariant, ReactNode> = {
  keypad: <InitiatePaymentKeypadSkeleton />,
  form: <InitiatePaymentFormSkeleton />,
};

type PaymentInitiatorVariant = "keypad" | "form";

type Props = {
  variant: PaymentInitiatorVariant;
  bankAccountPublicID?: string;
};

export const MerchantPaymentInitiator = ({
  variant,
  bankAccountPublicID,
}: Props) => {
  const [formErrors, setFormErrors] = useState<
    FieldErrors<InitiatePaymentFormData>
  >({});
  const {
    initiateTransaction,
    isPending,
    conflictingTransaction,
    resetConflictingTransaction,
  } = useInitiatePaypointPayment({
    onError: (error) => {
      const message = getTransactionErrorMessage(error, t);
      setFormErrors({ amount: { message, type: "custom" } });
      toast.error(message);
    },
  });

  const { data: paypoints, isLoading: isLoadingPaypoints } = usePayPointsQuery(
    0,
    100,
  );
  const { data, isLoading } = useBankingAccounts();
  const { isUserVerified, isLoading: isLoadingUserVerification } =
    useIsUserVerified();
  const { t } = useResource();
  const toAmountWithCurrency = useAmount();
  const marketCurrency = useUserMarketCurrency();

  if (isLoading || isLoadingPaypoints) {
    return <PrimaryPaper>{VARIANT_TO_SKELETON[variant]}</PrimaryPaper>;
  }

  if (!data || !paypoints) {
    return (
      <PrimaryPaper>
        {t(Resources.Merchant.Initiate.NoBankAccountsError)}
      </PrimaryPaper>
    );
  }

  const bankAccounts = data.items;
  const bankAccountByUrlParam =
    bankAccountPublicID &&
    bankAccounts.find(({ publicID }) => publicID === bankAccountPublicID);
  // Initiated amounts are minted in the market currency (useAmount) — both the keypad's
  // account and the form default must sit on that rail. Primary is per currency.
  const primaryBankAccount = bankAccounts.find(
    ({ isPrimary, currency }) => isPrimary && currency === marketCurrency,
  );
  const selectedBankAccount =
    bankAccountByUrlParam || pickMainBankAccount(bankAccounts, marketCurrency);

  // The URL may carry a stale or foreign account id; the form is seeded with the account that was
  // actually resolved, never with the raw parameter.
  const defaultValues = {
    publicID: selectedBankAccount?.publicID,
    bankAccountID: selectedBankAccount?.bankAccountID,
  };

  const isPreApproved = hasPreApprovedAccount(bankAccounts);
  const isDisabled =
    isLoadingUserVerification || (!isUserVerified && !isPreApproved);

  const onSubmit = ({
    amount,
    paypointID,
    bankAccountID,
  }: Omit<InitiatePaymentFormData, "publicID">) => {
    if (!amount) {
      return;
    }

    initiateTransaction({
      receiverBankAccountID: bankAccountID,
      payPointPublicID: paypointID,
      amount: toAmountWithCurrency(amount),
    });
  };

  if (variant === "keypad") {
    return (
      <PrimaryPaper>
        <InitiatePaymentKeypad
          isDisabled={isDisabled}
          bankAccountID={primaryBankAccount?.bankAccountID}
          onPay={(amount, paypointID) =>
            onSubmit({
              amount,
              paypointID,
            })
          }
          paypoints={paypoints.items}
        />
        {conflictingTransaction && (
          <ActiveTransactionConflictModal
            pendingTransaction={conflictingTransaction}
            onCancel={resetConflictingTransaction}
          />
        )}
      </PrimaryPaper>
    );
  }

  return (
    <PrimaryPaper sx={{ height: ["100%", "auto"] }}>
      <InitiatePaymentForm
        onSubmit={onSubmit}
        isSubmitting={isPending}
        bankAccounts={bankAccounts}
        defaultValues={defaultValues}
        isDisabled={isDisabled}
        errors={formErrors}
      />
    </PrimaryPaper>
  );
};
